import { Divider, Grid2, TextField, Typography } from "@mui/material";
import { Control, Controller } from "react-hook-form";
import { TolerancePicker } from "@/controls/TolerancePicker";
import { Clip } from "@/shared/models/Clip";
import { GradeSelector } from "@/shared/selectors/GradeSelector";
import { parseNumberInput } from "@/shared/utils/parseNumberInput";
import { ClipPunchSlotsField } from "./ClipPunchSlotsField";

type Props = {
	control: Control<Clip>;
	disabled: boolean;
};

const SectionHeader = ({ title }: { title: string }) => (
	<Grid2 size={{ xs: 12 }}>
		<Typography
			variant="subtitle2"
			fontWeight={600}
			color="primary"
			sx={{ mb: 0.5, mt: 1 }}
		>
			{title}
		</Typography>
		<Divider />
	</Grid2>
);

const SubHeader = ({ title }: { title: string }) => (
	<Grid2 size={{ xs: 12 }}>
		<Typography
			variant="caption"
			color="text.secondary"
			sx={{ display: "block", mt: 0.5 }}
		>
			{title}
		</Typography>
	</Grid2>
);

export const ClipDetailFields = ({ control, disabled }: Props) => {
	return (
		<>
			{/* ───────── Clip Info ───────── */}
			<SectionHeader title="Clip Info" />

			<Grid2 size={{ xs: 12, md: 6 }}>
				<Controller
					name="ClipPartNumber"
					control={control}
					render={({ field, fieldState: { error } }) => (
						<TextField
							label="Clip Part Number"
							value={field.value || ""}
							onChange={(e) => field.onChange(e.target.value || null)}
							fullWidth
							size="small"
							autoComplete="off"
							disabled={disabled}
							InputProps={{ readOnly: disabled }}
							error={!!error}
							helperText={error?.message}
						/>
					)}
				/>
			</Grid2>
			<Grid2 size={{ xs: 12, md: 6 }}>
				<Controller
					name="CustomerClipPartNumber"
					control={control}
					render={({ field }) => (
						<TextField
							label="Customer Clip Part Number"
							value={field.value || ""}
							onChange={(e) => field.onChange(e.target.value || null)}
							fullWidth
							size="small"
							autoComplete="off"
							disabled={disabled}
							InputProps={{ readOnly: disabled }}
						/>
					)}
				/>
			</Grid2>

			{/* Min | Target | Max, reading like the spec */}
			<Grid2 size={{ xs: 12, md: 5 }}>
				<Typography
					variant="caption"
					color="text.secondary"
					sx={{ display: "block", mb: 0.5 }}
				>
					Width
				</Typography>
				<Controller
					name="WidthTolerance"
					control={control}
					render={({ field }) => (
						<TolerancePicker
							value={field.value}
							onChange={(v) => field.onChange(v)}
							disabled={disabled}
							emptyOnNull
							middle={
								<Controller
									name="Width"
									control={control}
									rules={{
										required: "Width is required",
										min: {
											value: 0.001,
											message: "Width must be greater than 0",
										},
										max: { value: 99.999, message: "Width must be ≤ 99.999" },
									}}
									render={({ field: f, fieldState: { error } }) => (
										<TextField
											label="Width"
											type="number"
											value={f.value ?? ""}
											onChange={(e) =>
												f.onChange(parseFloat(e.target.value) || 0)
											}
											fullWidth
											size="small"
											disabled={disabled}
											InputProps={{ readOnly: disabled }}
											inputProps={{ min: 0.001, max: 99.999, step: "0.001" }}
											error={!!error}
											helperText={error?.message}
										/>
									)}
								/>
							}
						/>
					)}
				/>
			</Grid2>
			<Grid2 size={{ xs: 12, md: 5 }}>
				<Typography
					variant="caption"
					color="text.secondary"
					sx={{ display: "block", mb: 0.5 }}
				>
					Height
				</Typography>
				<Controller
					name="HeightTolerance"
					control={control}
					render={({ field }) => (
						<TolerancePicker
							value={field.value}
							onChange={(v) => field.onChange(v)}
							disabled={disabled}
							emptyOnNull
							middle={
								<Controller
									name="Height"
									control={control}
									rules={{
										required: "Height is required",
										min: {
											value: 0.001,
											message: "Height must be greater than 0",
										},
										max: { value: 99.999, message: "Height must be ≤ 99.999" },
									}}
									render={({ field: f, fieldState: { error } }) => (
										<TextField
											label="Height"
											type="number"
											value={f.value ?? ""}
											onChange={(e) =>
												f.onChange(parseFloat(e.target.value) || 0)
											}
											fullWidth
											size="small"
											disabled={disabled}
											InputProps={{ readOnly: disabled }}
											inputProps={{ min: 0.001, max: 99.999, step: "0.001" }}
											error={!!error}
											helperText={error?.message}
										/>
									)}
								/>
							}
						/>
					)}
				/>
			</Grid2>
			<Grid2 size={{ xs: 12, md: 2 }}>
				<Typography
					variant="caption"
					color="text.secondary"
					sx={{ display: "block", mb: 0.5 }}
				>
					&nbsp;
				</Typography>
				<Controller
					name="Gauge"
					control={control}
					render={({ field }) => (
						<TextField
							label="Gauge"
							type="number"
							value={field.value ?? ""}
							onChange={(e) =>
								field.onChange(
									parseNumberInput(e.target.value, { min: 0, max: 1.0 }),
								)
							}
							fullWidth
							size="small"
							disabled={disabled}
							InputProps={{ readOnly: disabled }}
							inputProps={{ min: 0, max: 1.0, step: "0.0001" }}
						/>
					)}
				/>
			</Grid2>
			<Grid2 size={{ xs: 12, md: 6 }}>
				<Controller
					name="MaterialGradeId"
					control={control}
					render={({ field, fieldState: { error } }) => (
						<GradeSelector
							label="Material Grade"
							value={field.value}
							onChange={(id) => field.onChange(id)}
							fieldError={error}
							disabled={disabled}
						/>
					)}
				/>
			</Grid2>
			<Grid2 size={{ xs: 6, md: 3 }}>
				<Controller
					name="CoilGauge"
					control={control}
					rules={{
						min: {
							value: 0.0001,
							message: "Coil Gauge must be greater than 0",
						},
						max: { value: 1.0, message: "Coil Gauge must be ≤ 1.0" },
					}}
					render={({ field, fieldState: { error } }) => (
						<TextField
							label="Coil Gauge"
							type="number"
							value={field.value ?? ""}
							onChange={(e) =>
								field.onChange(
									e.target.value === "" ? null : parseFloat(e.target.value),
								)
							}
							fullWidth
							size="small"
							disabled={disabled}
							InputProps={{ readOnly: disabled }}
							inputProps={{ min: 0.0001, max: 1.0, step: "0.0001" }}
							error={!!error}
							helperText={error?.message}
						/>
					)}
				/>
			</Grid2>
			<Grid2 size={{ xs: 6, md: 3 }}>
				<Controller
					name="CoilWidth"
					control={control}
					rules={{
						min: {
							value: 0.001,
							message: "Coil Width must be greater than 0",
						},
						max: { value: 999.999, message: "Coil Width must be ≤ 999.999" },
					}}
					render={({ field, fieldState: { error } }) => (
						<TextField
							label="Coil Width"
							type="number"
							value={field.value ?? ""}
							onChange={(e) =>
								field.onChange(
									e.target.value === "" ? null : parseFloat(e.target.value),
								)
							}
							fullWidth
							size="small"
							disabled={disabled}
							InputProps={{ readOnly: disabled }}
							inputProps={{ min: 0.001, max: 999.999, step: "0.001" }}
							error={!!error}
							helperText={error?.message}
						/>
					)}
				/>
			</Grid2>
			<Grid2 size={{ xs: 12, md: 6 }}>
				<Controller
					name="TestCheckCycle"
					control={control}
					render={({ field }) => (
						<TextField
							label="Test Check Cycle"
							type="number"
							value={field.value ?? ""}
							onChange={(e) =>
								field.onChange(
									parseNumberInput(e.target.value, {
										integer: true,
										min: 1,
										max: 100000,
									}),
								)
							}
							fullWidth
							size="small"
							disabled={disabled}
							InputProps={{ readOnly: disabled }}
							inputProps={{ min: 1, max: 100000, step: 1 }}
						/>
					)}
				/>
			</Grid2>

			<SubHeader title="Drawings" />
			<Grid2 size={{ xs: 6, md: 3 }}>
				<Controller
					name="DrawingNumber"
					control={control}
					render={({ field }) => (
						<TextField
							label="SWVA Drwg #"
							value={field.value || ""}
							onChange={(e) => field.onChange(e.target.value || null)}
							fullWidth
							size="small"
							autoComplete="off"
							disabled={disabled}
							InputProps={{ readOnly: disabled }}
						/>
					)}
				/>
			</Grid2>
			<Grid2 size={{ xs: 6, md: 3 }}>
				<Controller
					name="DrawingRevision"
					control={control}
					render={({ field }) => (
						<TextField
							label="Drwg. Rev. #"
							value={field.value || ""}
							onChange={(e) => field.onChange(e.target.value || null)}
							fullWidth
							size="small"
							autoComplete="off"
							disabled={disabled}
							InputProps={{ readOnly: disabled }}
						/>
					)}
				/>
			</Grid2>
			<Grid2 size={{ xs: 6, md: 3 }}>
				<Controller
					name="CustomerDrawingNumber"
					control={control}
					render={({ field }) => (
						<TextField
							label="Customer Drwg. #"
							value={field.value || ""}
							onChange={(e) => field.onChange(e.target.value || null)}
							fullWidth
							size="small"
							autoComplete="off"
							disabled={disabled}
							InputProps={{ readOnly: disabled }}
						/>
					)}
				/>
			</Grid2>
			<Grid2 size={{ xs: 6, md: 3 }}>
				<Controller
					name="CustomerDrawingRevision"
					control={control}
					render={({ field }) => (
						<TextField
							label="Customer Drwg. Rev. #"
							value={field.value || ""}
							onChange={(e) => field.onChange(e.target.value || null)}
							fullWidth
							size="small"
							autoComplete="off"
							disabled={disabled}
							InputProps={{ readOnly: disabled }}
						/>
					)}
				/>
			</Grid2>

			{/* ───────── Die Setup ───────── */}
			<SectionHeader title="Die Setup" />

			<Grid2 size={{ xs: 12 }}>
				<Controller
					name="HolePattern"
					control={control}
					rules={{ required: "Hole Pattern is required" }}
					render={({ field, fieldState: { error } }) => (
						<TextField
							label="Hole Pattern"
							value={field.value || ""}
							onChange={field.onChange}
							fullWidth
							size="small"
							autoComplete="off"
							disabled={disabled}
							InputProps={{ readOnly: disabled }}
							error={!!error}
							helperText={error?.message}
						/>
					)}
				/>
			</Grid2>
			<Grid2 size={{ xs: 12, md: 4 }}>
				<Controller
					name="DieSetDrawingNumber"
					control={control}
					render={({ field }) => (
						<TextField
							label="Die Set Drwg. #"
							value={field.value || ""}
							onChange={(e) => field.onChange(e.target.value || null)}
							fullWidth
							size="small"
							autoComplete="off"
							disabled={disabled}
							InputProps={{ readOnly: disabled }}
						/>
					)}
				/>
			</Grid2>
			<Grid2 size={{ xs: 12, md: 4 }}>
				<Controller
					name="DieSetDrawingRevision"
					control={control}
					render={({ field }) => (
						<TextField
							label="Die Set Drwg Revision #"
							value={field.value || ""}
							onChange={(e) => field.onChange(e.target.value || null)}
							fullWidth
							size="small"
							autoComplete="off"
							disabled={disabled}
							InputProps={{ readOnly: disabled }}
						/>
					)}
				/>
			</Grid2>
			<Grid2 size={{ xs: 12, md: 4 }}>
				<Controller
					name="DieSetNumber"
					control={control}
					render={({ field }) => (
						<TextField
							label="Die Set #"
							value={field.value || ""}
							onChange={(e) => field.onChange(e.target.value || null)}
							fullWidth
							size="small"
							autoComplete="off"
							disabled={disabled}
							InputProps={{ readOnly: disabled }}
						/>
					)}
				/>
			</Grid2>

			<ClipPunchSlotsField control={control} disabled={disabled} />

			{/* ───────── Comments ───────── */}
			<SectionHeader title="Comments" />
			<Grid2 size={{ xs: 12 }}>
				<Controller
					name="Comments"
					control={control}
					render={({ field }) => (
						<TextField
							value={field.value || ""}
							onChange={(e) => field.onChange(e.target.value || null)}
							fullWidth
							multiline
							minRows={2}
							inputProps={{ "aria-label": "Comments" }}
							size="small"
							autoComplete="off"
							disabled={disabled}
							InputProps={{ readOnly: disabled }}
						/>
					)}
				/>
			</Grid2>
		</>
	);
};
